import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Input } from './ui/input';
import { Pagination } from './ui/pagination';
import { Plus, Search, Eye, Edit, Trash2, Users, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { workerApiService, Worker } from '../services/workerApi';
import { PermissionGate } from './ui/PermissionGate';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';

export function WorkerListingPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const ROUTE = '/workers';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const branchId = selectedBranch || user?.defaultBranchId;
        const data = await workerApiService.getWorkers(branchId);
        setWorkers(data);
      } catch (e) {
        // toasted
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedBranch, user?.defaultBranchId]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return workers.filter(w =>
      w.workerCode?.toLowerCase().includes(term) ||
      w.name?.toLowerCase().includes(term) ||
      (w.department || '').toLowerCase().includes(term) ||
      (w.reportingManager || '').toLowerCase().includes(term) ||
      (w.workArea || '').toLowerCase().includes(term) ||
      (w.branch?.branchName || '').toLowerCase().includes(term) ||
      (w.skillLevel || '').toLowerCase().includes(term)
    );
  }, [workers, searchTerm]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const paginated = filtered.slice(startIndex, startIndex + pageSize);

  const handleNew = () => navigate('/workers/new');
  const handleEdit = (w: Worker) => navigate(`/workers/${w.id}/edit`);
  const handleView = (w: Worker) => navigate(`/workers/${w.id}/view`);
  const handleDelete = async (w: Worker) => {
    if (!w.id) return;
    if (window.confirm(`Delete worker ${w.name}?`)) {
      try {
        await workerApiService.deleteWorker(String(w.id));
        setWorkers(prev => prev.filter(x => x.id !== w.id));
      } catch (e) {
        // toasted
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span>Loading workers...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Worker Management</CardTitle>
                <p className="text-gray-600 text-sm">Manage worker master and assignments</p>
              </div>
            </div>
            <PermissionGate route={ROUTE} action="create">
              <Button onClick={handleNew} className="bg-primary-500 text-gray-700 hover:bg-gray-50 shadow-lg">
                <Plus className="h-4 w-4 mr-2" />
                New Worker
              </Button>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search workers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">{filtered.length} of {workers.length} workers</div>
          </div>

          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary-50">
                  <TableHead className="font-semibold text-primary-800">Name</TableHead>
                  <TableHead className="font-semibold text-primary-800">Work Area</TableHead>
                  <TableHead className="font-semibold text-primary-800 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                      No workers found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((w) => (
                    <TableRow key={w.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium">{w.name}</TableCell>
                      <TableCell>{(w as Worker & { workAreaName?: string }).workAreaName ?? w.workArea ?? '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <PermissionGate route={ROUTE} action="view">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(w)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROUTE} action="edit">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(w)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ROUTE} action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(w)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


